// solana-v1-operations.ts - the SIMD-0385 v1 half of the pre-built write lane.
//
// The realtime worker hands the SDK a serialized, possibly partially-signed
// transaction (sponsor and attestation signatures may already be attached). For
// v1 wires (first byte 0x81) @solana/web3.js's legacy/v0 classes cannot even
// deserialize the bytes, so this lane decodes with @solana/kit, runs the SAME
// semantic validation the v0 lane runs (program allow-list, setup-shape
// recomputation, OA-0001 intent binding), refreshes the lifetime only while the
// transaction is still unsigned, signs through the provider's `signTransactionV1`,
// and submits base64 over the same RPC handle the v0 lane uses.
//
// Lazy-loaded: `@solana/kit` must never enter the SDK's initial chunk.
import { Buffer } from 'buffer';
import { getConfig } from './config';
import { BoundedOnchainRuleRejection, decodeBase58, encodeBase58, errorText, isBoundedOnchainRuleRejection, isDefinitiveSolanaSendFailure, validateServerTransactionSemantics, } from './solana-operations';
import { getTransactionJson } from './solana-transaction-read';
function transactionHasSignature(transaction) {
    return Object.values(transaction.signatures).some((sig) => !!sig && sig.some((byte) => byte !== 0));
}
function sameBytes(a, b) {
    if (a.length !== b.length)
        return false;
    for (let i = 0; i < a.length; i += 1)
        if (a[i] !== b[i])
            return false;
    return true;
}
async function decodeAndValidate(serializedTransaction, options) {
    const kit = await import('@solana/kit');
    const transaction = kit.getTransactionDecoder().decode(Buffer.from(serializedTransaction, 'base64'));
    const compiledMessage = kit.getCompiledTransactionMessageDecoder().decode(transaction.messageBytes);
    if (compiledMessage.version !== 1) {
        throw new Error(`${options.label} announced SIMD-0385 v1 but decoded as version ${String(compiledMessage.version)}`);
    }
    if (compiledMessage.instructionHeaders.length !== compiledMessage.instructionPayloads.length) {
        throw new Error(`${options.label} has mismatched v1 instruction headers and payloads`);
    }
    const { PublicKey } = await import('@solana/web3.js');
    const accountKeys = compiledMessage.staticAccounts.map((key) => new PublicKey(key));
    const instructions = compiledMessage.instructionHeaders.map((header, index) => {
        if (header.programAccountIndex >= accountKeys.length)
            throw new Error(`${options.label} has an invalid program account index`);
        const payload = compiledMessage.instructionPayloads[index];
        return {
            programId: accountKeys[header.programAccountIndex].toBase58(),
            accountKeyIndexes: [...payload.instructionAccountIndices],
            data: Uint8Array.from(payload.instructionData),
        };
    });
    validateServerTransactionSemantics(Object.assign(Object.assign({}, options), { accountKeys, isAccountSigner: (index) => index >= 0 && index < compiledMessage.header.numSignerAccounts, instructions, PublicKeyCtor: PublicKey }));
    return { transaction, compiledMessage, kit };
}
/**
 * LF-003 parity with the v0 lane: an UNSIGNED transaction is re-based on a live
 * blockhash before the wallet signs it; a transaction that already carries a
 * co-signer's signature must keep the exact message it was co-signed against,
 * and its fence must name that same blockhash.
 */
async function refreshLifetime(transaction, compiledMessage, kit, rpc, serverFence) {
    var _a;
    const fence = { blockhash: serverFence.blockhash, lastValidBlockHeight: serverFence.lastValidBlockHeight };
    if (transactionHasSignature(transaction)) {
        if (String(compiledMessage.lifetimeToken) !== fence.blockhash) {
            throw new Error(`The pre-built v1 transaction was co-signed against blockhash ${String(compiledMessage.lifetimeToken)} ` +
                `but its expiry fence names ${fence.blockhash}. Nothing was signed.`);
        }
        return { transaction, fence };
    }
    if (!rpc)
        return { transaction, fence };
    if (typeof rpc.getLatestBlockhash !== 'function') {
        throw new Error('Pre-built v1 signing needs a current blockhash, but the configured Solana RPC cannot report one. Nothing was signed.');
    }
    const latest = await rpc.getLatestBlockhash('confirmed');
    const decoded = decodeBase58(String((_a = latest === null || latest === void 0 ? void 0 : latest.blockhash) !== null && _a !== void 0 ? _a : ''));
    if (decoded === null || decoded.length !== 32) {
        throw new Error(`The configured Solana RPC returned "${latest === null || latest === void 0 ? void 0 : latest.blockhash}" as the current blockhash, which is not a 32-byte base58 value. Nothing was signed.`);
    }
    const decompiled = kit.decompileTransactionMessage(compiledMessage, { lastValidBlockHeight: BigInt(serverFence.lastValidBlockHeight) });
    const refreshed = kit.setTransactionMessageLifetimeUsingBlockhash({ blockhash: kit.blockhash(latest.blockhash), lastValidBlockHeight: BigInt(latest.lastValidBlockHeight) }, decompiled);
    return {
        transaction: kit.compileTransaction(refreshed),
        fence: { blockhash: latest.blockhash, lastValidBlockHeight: latest.lastValidBlockHeight },
    };
}
export async function handlePreBuiltV1Transaction(tx, authProvider, options, expectedIntent) {
    var _a, _b, _c, _d, _e, _f, _g, _h, _j, _k;
    const config = (_b = (_a = options === null || options === void 0 ? void 0 : options._overrides) === null || _a === void 0 ? void 0 : _a._config) !== null && _b !== void 0 ? _b : await getConfig();
    const decoded = await decodeAndValidate(tx.serializedTransaction, {
        label: 'Pre-built v1 transaction', expectedAppId: config.appId, expectedIntent,
    });
    if (typeof authProvider.signTransactionV1 !== 'function') {
        // The worker only builds v1 for a client that advertised it; reaching this
        // means the provider changed underneath the write. Refuse before any RPC.
        throw new Error('The write came back as a Solana transaction v1 (SIMD-0385) but this auth provider cannot sign v1. Nothing was signed.');
    }
    const shouldSubmit = (options === null || options === void 0 ? void 0 : options.shouldSubmitTx) !== false;
    const rpcUrl = (_c = config.rpcUrl) === null || _c === void 0 ? void 0 : _c.trim();
    const injectedRpc = (_d = options === null || options === void 0 ? void 0 : options._overrides) === null || _d === void 0 ? void 0 : _d._solanaRpc;
    if (shouldSubmit && !rpcUrl && !injectedRpc) {
        throw new Error(`Pre-built Solana transaction submission requires init({ rpcUrl }) for ${tx.network}. ` +
            `Pass a TOP-LEVEL rpcUrl (with chain) to init(), e.g. ` +
            `init({ appId, chain: '${tx.network}', rpcUrl: '<your ${tx.network} RPC endpoint>' }); ` +
            `a nested walletLogin.rpcUrl only configures wallet login and does not enable submission.`);
    }
    const { Connection } = await import('@solana/web3.js');
    const connection = injectedRpc !== null && injectedRpc !== void 0 ? injectedRpc : (rpcUrl ? new Connection(rpcUrl, 'confirmed') : null);
    const refreshed = await refreshLifetime(decoded.transaction, decoded.compiledMessage, decoded.kit, connection, tx);
    const signedTx = await authProvider.signTransactionV1(refreshed.transaction);
    if (!sameBytes(signedTx.messageBytes, refreshed.transaction.messageBytes)) {
        throw new Error('The signer changed the v1 transaction message instead of signing the exact transaction. Nothing was submitted.');
    }
    const feePayerAddress = String((_e = decoded.compiledMessage.staticAccounts[0]) !== null && _e !== void 0 ? _e : '');
    const feePayerSignature = signedTx.signatures[feePayerAddress];
    const rawTx = Uint8Array.from(decoded.kit.getTransactionEncoder().encode(signedTx));
    const signedTransaction = Buffer.from(rawTx).toString('base64');
    if (!shouldSubmit) {
        return { outcome: 'signed', lane: 'solana', transactionSignature: null, signedTransaction, fence: refreshed.fence };
    }
    if (!feePayerSignature || feePayerSignature.every((byte) => byte === 0)) {
        throw new Error('The signed v1 transaction carries no fee-payer signature; it cannot be submitted.');
    }
    // The transaction id is the fee payer's signature, derived from the signed bytes
    // BEFORE broadcasting so a lost send response still yields a reconcilable id.
    const signature = encodeBase58(Uint8Array.from(feePayerSignature));
    const submitRpc = connection;
    try {
        await submitRpc.sendRawTransaction(rawTx, { skipPreflight: false, maxRetries: 3 });
    }
    catch (error) {
        if (isDefinitiveSolanaSendFailure(error)) {
            if (isBoundedOnchainRuleRejection(error))
                throw new BoundedOnchainRuleRejection(error);
            throw error;
        }
        return {
            outcome: 'submitted', lane: 'solana', transactionSignature: signature, signedTransaction, fence: refreshed.fence,
            reason: `Broadcast response was lost or inconclusive: ${errorText(error)}`,
        };
    }
    let confirmation;
    try {
        confirmation = await submitRpc.confirmTransaction({ signature, blockhash: refreshed.fence.blockhash, lastValidBlockHeight: refreshed.fence.lastValidBlockHeight }, 'confirmed');
    }
    catch (error) {
        return {
            outcome: 'submitted', lane: 'solana', transactionSignature: signature, signedTransaction, fence: refreshed.fence,
            reason: `Confirmation outcome unknown: ${errorText(error)}`,
        };
    }
    if ((_f = confirmation.value) === null || _f === void 0 ? void 0 : _f.err) {
        let logMessages;
        try {
            logMessages = (_h = (_g = (await getTransactionJson(submitRpc, signature, 'confirmed'))) === null || _g === void 0 ? void 0 : _g.meta) === null || _h === void 0 ? void 0 : _h.logMessages;
        }
        catch (_l) {
            // Diagnostic only; the on-chain error is the verdict.
        }
        if (isBoundedOnchainRuleRejection(confirmation.value.err, logMessages)) {
            throw new BoundedOnchainRuleRejection(confirmation.value.err, logMessages);
        }
        throw new Error(`Transaction failed: ${logMessages ? JSON.stringify(logMessages) : JSON.stringify(confirmation.value.err)}`);
    }
    return {
        outcome: 'confirmed', lane: 'solana', transactionSignature: signature, signedTransaction, fence: refreshed.fence,
        confirmationContextSlot: (_k = (_j = confirmation.context) === null || _j === void 0 ? void 0 : _j.slot) !== null && _k !== void 0 ? _k : 0,
    };
}
